import React, {ChangeEvent, useCallback, useMemo, useState} from 'react'
import {
  Button,
  Divider,
  DropdownProps,
  Form, Header,
  Modal,
  Select,
  Table,
  TableCell,
  TableRow,
} from 'semantic-ui-react'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { currencyFormatter } from '../../common/util'
import {AP_PRODUCT_TYPES, APCartItem, Cart, CartError} from './ap.types'
import {clearCart, selectCart, updateCartDetail, updateCartItem} from './apCart.slice'
import cartServiceAPI from "../../apis/CartServiceAPI";
import {AERIAL_PHOTO_SERVICES_EMAIL, RECAPTCHA_ENABLED} from "../../environment";
import {unitsFactors} from "@turf/helpers";
import {isEmpty, isUndefined} from "lodash";
import {generateRecaptchaToken} from "../../common/recapctcha";

const CheckoutModal = () => {
  const dispatch = useAppDispatch()

  const [open, setOpen] = React.useState(false)

  const initialCartErrorState = (): CartError => {
    return {
      id: 0,
      error: false,
      message: ''
    }
  }

  const cartState = useAppSelector(selectCart)
  const [cartError, setCartError] = useState<CartError>(initialCartErrorState)

  const onChangeInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(updateCartDetail({ name: event.target.name, value: event.target.value }))
  }

  const onSubmit = () => {

    if (validationFailed)
    {
      return false;
    }

    let cart = {
      orderDate: new Date().toLocaleString("en-AU"),
      contactName: cartState.contactName,
      department: cartState.department,
      phoneNo: cartState.phoneNo,
      email: cartState.email,
      instructions: cartState.instructions,
      items: cartState.items
    } as Cart;

    cartServiceAPI.postCart(cart)
        .then(() => {
          dispatch(clearCart());
          setOpen(false)
        })
        .catch((e) => {
          const errorMessage = `
          An error occurred submitting your cart. Please try again.
          If the problem persists please contact ${AERIAL_PHOTO_SERVICES_EMAIL} 
          `

          if (e.response && e.response.data)
          {
            let error = e.response.data;
            let errorId = error.error.id
            let fullMessage = errorMessage + `and quote reference number ${errorId}`
            setCartError ({id: errorId, error: true, message: fullMessage})
          }
          else
          {
            setCartError({id: 1, error: true, message: errorMessage})}
          }
        )

  }

  const validationFailed = useMemo( () => {
    return isEmpty(cartState.phoneNo) ||
        isEmpty(cartState.contactName) ||
        isEmpty(cartState.email)
  }, [cartState.phoneNo, cartState.contactName, cartState.email])

  return (
    <Modal
      size={'small'}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      open={open}
      closeIcon={{style: { top: '0.6rem', left: '1rem'}, color: 'black', name:'close'}}
      trigger={<Button primary>Checkout</Button>}
    >
      <Modal.Header>
        <Header as={"h3"} textAlign={"center"}>
          Request Quote
        </Header>
      </Modal.Header>
      <Modal.Content>
        <div>
          <Table compact className={'apCheckoutTable'}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Project</Table.HeaderCell>
                <Table.HeaderCell>Film</Table.HeaderCell>
                <Table.HeaderCell>Run</Table.HeaderCell>
                <Table.HeaderCell>Frame #</Table.HeaderCell>
                <Table.HeaderCell>Product Required</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {cartState.items.map((item, index) => <CheckoutItem item={item} key={item.frame.id}/>)}
            </Table.Body>
          </Table>
        </div>
          <div style={{
            display: 'flex',
            marginTop: '16px',
            justifyContent: 'space-between',
          }}>
            <div>
              <p>
                On submission of a quote, your order will be directed to either Libraries & Archives NT (LANT) and/or
                the
                Department of Infrastructure, Planning & Logistics (DIPL);
              </p>
              <p>
                <strong>Film-based photos – Pre 2008</strong> (includes some 2008) are provided by LANT. <br/>
                A quote for provision of copies of historic archived aerial images will be provided on submission of a
                quotation, and details of copying fees for historic images (pre 2008) are on the LANT website
                at <a href={'https://lant.nt.gov.au/copying-services'}>https://lant.nt.gov.au/copying-services</a>.
              </p>
              <p>
                <strong>Digital photos – Post 2008</strong> (includes some 2008) are provided by DIPL. <br/>
                A quote and licensing terms and conditions will be provided on submission of a quotation.
                In some cases it may be more convenient to supply seamless orthoimagery that covers the photo frames
                selected. On submission of a quotation, we will discuss the best option with you.
              </p>
            </div>
            <div style={{
              marginRight: '8px',
              textAlign: 'right',
            }}>
            </div>
          </div>

          <Divider/>
          <Form size={'small'}>
            <Form.Field required error={isEmpty(cartState.contactName)}>
              <label>Contact Name</label>
              <input
                  name={'contactName'}
                  value={cartState.contactName}
                  onChange={onChangeInput}/>
            </Form.Field>
            <Form.Field required error={isEmpty(cartState.email)}>
              <label>Email Address</label>
              <input
                  name={'email'}
                  value={cartState.email}
                  placeholder="someone@example.com" type={'email'}
                  onChange={onChangeInput}/>
            </Form.Field>
            <Form.Field required error={isEmpty(cartState.phoneNo)}>
              <label>Phone</label>
              <input
                  name={'phoneNo'}
                  value={cartState.phoneNo}
                  onChange={onChangeInput}/>
            </Form.Field>
            <Form.Field>
              <label>Organisation</label>
              <input
                  name={'department'}
                  value={cartState.department}
                  onChange={onChangeInput}/>
            </Form.Field>
            <Form.Field>
              <label>Additional Information</label>
              <textarea
                  name={'instructions'}
                  value={cartState.instructions}
                  onChange={onChangeInput}/>
            </Form.Field>
          </Form>

          {cartError.error &&
              <div className={"ui negative message"}>
                <div className={"header"}> An error has occurred</div>
                <p> {cartError.message} </p>
              </div>
          }

        </Modal.Content>
        <Modal.Actions>
          <Button type='submit' primary onClick={onSubmit} disabled={validationFailed}>
            Confirm Checkout
          </Button>
        </Modal.Actions>
      </Modal>
  )
}

type CheckoutItemProps = {
  item: APCartItem
}

const CheckoutItem = ({item}: CheckoutItemProps) => {

  const dispatch = useAppDispatch()

  const productOptions: { key: string, value: AP_PRODUCT_TYPES, text: string }[] = [
    { key: 'tiff600', value: 'tiff600', text: 'TIFF - 600 DPI' },
    { key: 'jpeg600', value: 'jpeg600', text: 'JPEG - 600 DPI' },
  ]

  const handleChangeProduct = (event: any, data: DropdownProps) => {
    const newValue = data.value as AP_PRODUCT_TYPES
    if (newValue) {
      dispatch(updateCartItem({
        ...item,
        product: newValue,
      }))
    }
  }

  return (
    <TableRow>
      <TableCell>{item.frame.project}</TableCell>
      <TableCell>{item.frame.film_type} {item.frame.film}</TableCell>
      <TableCell>{item.frame.run}</TableCell>
      <TableCell>{item.frame.frame}</TableCell>
      <TableCell>
        <Select
          fluid
          value={item.product}
          options={productOptions}
          onChange={handleChangeProduct}/>
      </TableCell>
    </TableRow>
  )
}

export default CheckoutModal